import WebAnalytics, { BaseEvent, CustomerType, EventCategory, WebAnalyticsOptions } from '@lendi/analytics-web';
import WebAnalyticsV4 from '@lendi/analytics-web-v4';
import { SessionState } from '@lendi/lala-react';
import { Brand } from '@lendi/lala-utils';
import { datadogLogs } from '@datadog/browser-logs';

type BaseEventWithNoCategory = Omit<BaseEvent, 'category'> & Partial<Pick<BaseEvent, 'category'>>;

export class WebAnalyticsWrapped {
  private analytics: WebAnalytics | WebAnalyticsV4;

  constructor(private category: EventCategory, args: WebAnalyticsOptions, analyticsWebV4: boolean = false) {
    this.analytics = analyticsWebV4 ? new WebAnalyticsV4(args) : new WebAnalytics(args);
  }

  private createTraits() {
    return {
      user_type: CustomerType.CUSTOMER,
    };
  }

  public async trackPageLoad(name: string): Promise<void> {
    try {
      await this.analytics.trackPage(this.category, name);
    } catch (err) {
      datadogLogs.logger.error(err as any);
    }
  }

  public async trackEventOccurred<T extends BaseEventWithNoCategory>(event: T): Promise<void> {
    try {
      await (this.analytics.trackEvent as any)({
        category: this.category,
        ...event,
      });
    } catch (err) {
      datadogLogs.logger.error(err as any);
    }
  }

  async trackCustomer(customerId?: string, flags?: any, userTraits?: any): Promise<void> {
    try {
      const baseTraits = this.createTraits();
      const traits = { ...baseTraits, ...userTraits };
      await this.analytics.identify(customerId || '', flags || {}, traits);
    } catch (err) {
      datadogLogs.logger.error(err as any);
    }
  }
}

export function createWebAnalytics(category: EventCategory, session?: SessionState, analyticsWebV4?: boolean) {
  const isAussie = session
    ? session?.brand === Brand.Aussie
    : (window?.location?.hostname || '').indexOf('aussie') !== -1;

  const writeKey = isAussie ? process.env.REACT_APP_AUSSIE_SEGMENT_ID : process.env.REACT_APP_SEGMENT_ID;
  const writeKeyV4 = isAussie ? process.env.REACT_APP_AUSSIE_AMPLITUDE_KEY : process.env.REACT_APP_LENDI_AMPLITUDE_KEY;

  const selectedWriteKey = analyticsWebV4 ? writeKeyV4 : writeKey;

  if (!selectedWriteKey) {
    throw new Error(
      `Missing ${analyticsWebV4 ? 'Amplitude' : 'Segment'} write key for ${isAussie ? 'Aussie' : 'Lendi'}`
    );
  }

  return new WebAnalyticsWrapped(
    category,
    {
      writeKey: selectedWriteKey,
    },
    analyticsWebV4
  );
}
